import type { Rail } from "../level";
import type { Checker, Obj } from "../mapcheck";
import type { Prim } from "./index";
import type { Scenery } from "./scenery";

// What every kind of park primitive and map piece has to provide. Each kind
// lives in its own file here; `index.ts` lists them all, typed so that a kind
// missing from a list doesn't compile. Meshes are the one thing kept out of
// here (they need three.js, and the sim stays free of it): see PRIM_MESHES in
// render/level-meshes.ts.

/** The fields every primitive has: placed at (x, z), turned by `rot` about y, `hw` half-wide across. */
export interface Placed {
  x: number;
  z: number;
  rot: number;
  hw: number;
}

/** A primitive's height and slope at a point in its local frame (u along its ramp, w across). */
export interface Local {
  h: number;
  du: number;
  dw: number;
  /**
   * On a quarter pipe's transition: how far up it (u) and its radius, for
   * vert. Up the transition is +u unless `axis` says otherwise (a bowl's
   * walls face every way, #11), as a local unit direction. `curved` where
   * the wall bends round (a bowl's corner), so which way is up changes as
   * you move along it.
   */
  quarter?: { u: number; r: number; axis?: { u: number; w: number }; curved?: boolean };
  /** In a water slide's chute (#163): the way it runs (local unit direction), which the ride follows. */
  guide?: { u: number; w: number };
}

/** How the picker thumbnail colors a patch of a primitive. */
export type PlanFill = "box" | "kicker" | "ramp" | "deck";

export interface PrimKind<P extends Placed> {
  /** Height and slope at local (u, w), or null where the primitive doesn't cover the point. */
  eval(p: P, u: number, w: number): Local | null;
  /** The local u range it covers; it spans ±hw across unless `across` says otherwise. */
  extent(p: P): [u0: number, u1: number];
  /** The local w range it covers, where that isn't ±hw (an L pool's arm, #169). */
  across?(p: P): [w0: number, w1: number];
  /** How dark its baked contact shadow is where it meets the floor (0..1). */
  shadow: number;
  /** How tall it stands, so taller pieces draw over lower ones on the thumbnail. */
  top(p: P): number;
  /** Its top-down plan for the thumbnail: patches along u, each ±hw across (or its own `hw`, about its own `w`). */
  plan(p: P): { u0: number; u1: number; w?: number; hw?: number; fill: PlanFill }[];
}

/** What a map piece compiles into. */
export interface Compiled {
  prims: Prim[];
  rails: Rail[];
  /** Drawn only (#156): rides, buildings and furniture the sim never sees. */
  scenery: Scenery[];
}

/** How far out a piece may stand along x and z. */
export interface PieceReach {
  x: { min: number; max: number };
  z: { min: number; max: number };
}

export interface PieceKind<T> {
  /** Every field the piece allows besides `kind`; anything else is flagged as a likely typo. */
  fields: readonly string[];
  /** Has x, z, an optional rot and hw, checked before `check` runs. */
  placed: boolean;
  /**
   * Checks the piece's own fields, adding every problem to `c`. `reach` is how
   * far out along x and z a placed piece may stand: the fence, or out past a
   * beach side over its sand and sea (a pier, #221).
   */
  check(c: Checker, o: Obj, path: string, bounds: number, reach: PieceReach): void;
  /** Adds the checked piece's primitives, rails and scenery to `out`, in order. */
  compile(piece: T, out: Compiled): void;
}

/**
 * A piece's own color (#115), for the renderer only: the sim never reads
 * it. Copied onto the primitive only when the file gives one, so a map
 * without it compiles exactly as before.
 */
export function withColor<T extends object>(prim: T, color: string | undefined): T {
  return color === undefined ? prim : { ...prim, color };
}

/** Checks a piece's optional `color`. */
export function checkColor(c: Checker, o: Obj, path: string): void {
  if (o.color !== undefined) c.color(o, "color", path);
}

/** Map files use degrees; the sim uses radians. */
export const rad = (deg: number | undefined): number => ((deg ?? 0) * Math.PI) / 180;
